import React, { useState } from "react";
import { motion } from "framer-motion";

const Secourisme = () => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('urgences');

  const categories = [
    { id: 'urgences', name: 'Urgences vitales', icon: '🚨' },
    { id: 'maladies', name: 'Maladies tropicales', icon: '🌡️' },
    { id: 'morsures', name: 'Morsures et piqûres', icon: '🐍' },
    { id: 'environnement', name: 'Risques environnementaux', icon: '🌍' }
  ];

  const guides = {
    urgences: [
      {
        id: 1,
        title: "Arrêt cardiaque",
        icon: "💗",
        steps: [
          "Vérifier l'état de conscience",
          "Appeler les secours locaux",
          "Commencer la réanimation cardio-pulmonaire",
          "30 compressions thoraciques suivies de 2 insufflations",
          "Continuer jusqu'à l'arrivée des secours"
        ]
      },
      {
        id: 2,
        title: "Étouffement",
        icon: "🫁",
        steps: [
          "Identifier si la personne peut parler ou tousser",
          "Effectuer 5 tapes dans le dos",
          "Si inefficace, faire la manœuvre de Heimlich",
          "Alterner entre les tapes et la manœuvre",
          "Appeler les secours si pas d'amélioration"
        ]
      },
      {
        id: 3,
        title: "Saignement abondant",
        icon: "🩸",
        steps: [
          "Appuyer fortement sur la plaie",
          "Allonger la victime",
          "Maintenir la compression",
          "Appeler les secours",
          "Ne pas retirer le pansement compressif"
        ]
      }
    ],
    maladies: [
      {
        id: 4,
        title: "Paludisme",
        icon: "🦟",
        steps: [
          "Reconnaître les symptômes (fièvre, maux de tête, frissons)",
          "Faire un test de diagnostic rapide si disponible",
          "Commencer le traitement antipaludéen rapidement",
          "Maintenir une bonne hydratation",
          "Consulter un professionnel de santé"
        ]
      },
      {
        id: 5,
        title: "Déshydratation",
        icon: "💧",
        steps: [
          "Préparer une solution de réhydratation orale",
          "Faire boire régulièrement par petites quantités",
          "Surveiller les signes vitaux",
          "Maintenir la personne au frais",
          "Consulter si les symptômes persistent"
        ]
      },
      {
        id: 6,
        title: "Fièvre Typhoïde",
        icon: "🤒",
        steps: [
          "Isoler la personne malade",
          "Assurer une bonne hygiène",
          "Donner des antipyrétiques si forte fièvre",
          "Maintenir une hydratation adéquate",
          "Consulter rapidement un médecin"
        ]
      }
    ],
    morsures: [
      {
        id: 7,
        title: "Morsure de serpent",
        icon: "🐍",
        steps: [
          "Garder la victime calme et immobile",
          "Enlever tout ce qui peut serrer (bijoux, montre)",
          "Ne pas faire de garrot",
          "Ne pas tenter d'aspirer le venin",
          "Transport urgent vers l'hôpital"
        ]
      },
      {
        id: 8,
        title: "Piqûre de scorpion",
        icon: "🦂",
        steps: [
          "Nettoyer la zone piquée à l'eau et au savon",
          "Appliquer du froid",
          "Surélever le membre atteint",
          "Surveiller les signes d'allergie",
          "Consulter rapidement un médecin"
        ]
      },
      {
        id: 9,
        title: "Morsure d'animal",
        icon: "🐕",
        steps: [
          "Laver abondamment à l'eau et au savon",
          "Désinfecter la plaie",
          "Vérifier la vaccination antirabique",
          "Consulter rapidement un médecin",
          "Surveiller l'évolution de la plaie"
        ]
      }
    ],
    environnement: [
      {
        id: 10,
        title: "Coup de chaleur",
        icon: "☀️",
        steps: [
          "Placer la personne à l'ombre",
          "Rafraîchir avec de l'eau tiède",
          "Faire boire régulièrement",
          "Ventiler la personne",
          "Appeler les secours si état grave"
        ]
      },
      {
        id: 11,
        title: "Inondation",
        icon: "🌊",
        steps: [
          "Se réfugier en hauteur",
          "Ne pas traverser les zones inondées",
          "Couper l'électricité",
          "Préparer un kit d'urgence",
          "Suivre les consignes des autorités"
        ]
      },
      {
        id: 12,
        title: "Pollution de l'eau",
        icon: "💧",
        steps: [
          "Faire bouillir l'eau avant consommation",
          "Utiliser des filtres si disponibles",
          "Stocker l'eau dans des récipients propres",
          "Éviter les sources d'eau stagnante",
          "Utiliser des pastilles de purification"
        ]
      }
    ]
  };

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Guide des Premiers Secours
        </h1>
        <p className="text-xl text-gray-600">
          Apprenez les gestes qui sauvent et soyez prêt à agir en cas d'urgence
        </p>
      </motion.div>

      {/* Sélection de catégorie */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {categories.map((category) => (
          <motion.button
            key={category.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-6 py-3 rounded-full flex items-center space-x-2 ${
              selectedCategory === category.id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } shadow-md transition-colors`}
          >
            <span>{category.icon}</span>
            <span>{category.name}</span>
          </motion.button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {guides[selectedCategory].map((guide, index) => (
          <motion.div
            key={guide.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer"
            onClick={() => setSelectedGuide(selectedGuide === guide.id ? null : guide.id)}
            whileHover={{ scale: 1.02 }}
          >
            <div className="p-6">
              <div className="text-4xl mb-4">{guide.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {guide.title}
              </h3>
              
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: selectedGuide === guide.id ? "auto" : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-2">
                  {guide.steps.map((step, stepIndex) => (
                    <motion.div
                      key={stepIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: stepIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-sm font-medium mr-3">
                        {stepIndex + 1}
                      </span>
                      <p className="text-gray-600 text-left">{step}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.button
                animate={{ rotate: selectedGuide === guide.id ? 180 : 0 }}
                className="mt-4 text-blue-700 hover:text-blue-800"
              >
                {selectedGuide === guide.id ? "▲" : "▼"}
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-12 bg-red-50 border border-red-200 rounded-lg p-6"
      >
        <h2 className="text-2xl font-semibold text-red-700 mb-4 text-center">
          ⚠️ Numéros d'urgence par pays
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Sénégal</h3>
            <div className="space-y-1 text-gray-600">
              <p>SAMU : 1515</p>
              <p>Police : 17</p>
              <p>Pompiers : 18</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Côte d'Ivoire</h3>
            <div className="space-y-1 text-gray-600">
              <p>SAMU : 185</p>
              <p>Police : 110 / 111</p>
              <p>Pompiers : 180</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Cameroun</h3>
            <div className="space-y-1 text-gray-600">
              <p>SAMU : 119</p>
              <p>Police : 117</p>
              <p>Pompiers : 118</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Maroc</h3>
            <div className="space-y-1 text-gray-600">
              <p>SAMU : 15</p>
              <p>Police : 19</p>
              <p>Pompiers : 15</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold text-gray-900 mb-2">Congo</h3>
            <div className="space-y-1 text-gray-600">
              <p>SAMU : 118</p>
              <p>Police : 117</p>
              <p>Pompiers : 118</p>
              <p>Urgences : 122</p>
            </div>
          </div>
        </div>
        <p className="mt-6 text-red-600 text-center text-sm">
          Ces informations sont données à titre indicatif. Vérifiez les numéros d'urgence locaux de votre région.
          En cas de doute, contactez le centre de santé le plus proche.
        </p>
      </motion.div>

      {/* Section de téléchargement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-12 bg-blue-50 rounded-lg p-6 text-center"
      >
        <h2 className="text-2xl font-semibold text-blue-700 mb-4">
          📱 Application mobile (en cours de développement)
        </h2>
        <p className="text-gray-600 mb-4">
          Téléchargez notre application mobile pour avoir accès à ces guides même sans connexion internet.
          Disponible en français et dans plusieurs langues locales.
        </p>
        <div className="flex justify-center space-x-4">
          <button className="bg-black text-white px-6 py-3 rounded-lg flex items-center space-x-2">
            <span>App Store</span>
          </button>
          <button className="bg-black text-white px-6 py-3 rounded-lg flex items-center space-x-2">
            <span>Play Store</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Secourisme;